"use strict";

// Live Ready-for-Playback display — uses API playbackIntegrity when present.
// Do NOT use CHECKPOINT_LABELS['ready-for-playback'] alone for the live column
// when governance enrichment is on the row (historical checkpoint ≠ live RFP).
//
// PJH — for terminal failed / not_approved / upload_expired jobs, Live Status
// must reflect the terminal outcome — never a pending checkpoint label like
// "Track Record Finalized" that implies successful Track creation.

import {
  CHECKPOINT_LABELS,
  type PlaybackIntegrity,
  type PlaybackIntegrityFailure,
  type ProcessingJobRow,
  type ProcessingJobState,
} from "./processingTypes";

export type LivePlaybackDisplayTone = "ready" | "issue" | "checkpoint";

export interface LivePlaybackDisplay {
  label: string;
  tone: LivePlaybackDisplayTone;
  /** Short operator summary (Integrity Issue failures). */
  summary: string | null;
}

export function summarizeIntegrityFailures(
  failures: PlaybackIntegrityFailure[] | null | undefined,
  max = 2,
): string | null {
  if (!failures || failures.length === 0) return null;
  const parts = failures
    .slice(0, max)
    .map((f) => (f && typeof f.message === "string" ? f.message : f?.code))
    .filter(Boolean);
  if (parts.length === 0) return null;
  const extra = failures.length > max ? ` (+${failures.length - max} more)` : "";
  return `${parts.join("; ")}${extra}`;
}

function isTrackless(job: {
  trackId?: string | null;
  lineageTrackId?: string | null;
}): boolean {
  return !job.trackId && !job.lineageTrackId;
}

/**
 * Live status cell for Song Processing list / detail framing.
 * When `playbackIntegrity` is absent (older API), falls back to checkpoint label
 * for in-flight / approved paths — but never for terminal failure outcomes.
 */
export function livePlaybackDisplay(
  job: Pick<
    ProcessingJobRow,
    "playbackIntegrity" | "currentCheckpoint" | "ownership"
  > & {
    state?: ProcessingJobState | null;
    trackId?: string | null;
    lineageTrackId?: string | null;
  },
): LivePlaybackDisplay {
  const state = job.state ?? undefined;

  // Terminal outcomes own Live Status — checkpoint keys can still be pending
  // after a mid-finalize stall (e.g. track-finalized begun, never completed).
  if (state === "failed") {
    return {
      label: isTrackless(job)
        ? "Failed — no Track created"
        : "Failed",
      tone: "issue",
      summary: null,
    };
  }
  if (state === "not_approved") {
    return {
      label: isTrackless(job)
        ? "Not Approved — no Track created"
        : "Not Approved",
      tone: "issue",
      summary: null,
    };
  }
  if (state === "upload_expired") {
    return {
      label: "Upload Expired",
      tone: "issue",
      summary: null,
    };
  }

  const integrity: PlaybackIntegrity | null | undefined = job.playbackIntegrity;
  if (integrity && integrity.status === "ready_for_playback") {
    return {
      label: "Ready for Playback",
      tone: "ready",
      summary: null,
    };
  }
  if (integrity && integrity.status === "integrity_issue") {
    return {
      label: "Integrity Issue",
      tone: "issue",
      summary: summarizeIntegrityFailures(integrity.failures),
    };
  }
  // not_applicable or missing enrichment — historical / operational checkpoint.
  const cp = job.currentCheckpoint;
  return {
    label: cp ? CHECKPOINT_LABELS[cp] ?? cp : "—",
    tone: "checkpoint",
    summary: null,
  };
}

/**
 * Historical Checkpoint label for detail — checkpoint names are past-tense
 * success phrases; qualify when the step was only started (pending) or failed
 * so operators do not read "Track Record Finalized" as Track creation success.
 */
export function historicalCheckpointDisplay(job: {
  currentCheckpoint?: string | null;
  state?: ProcessingJobState | null;
  checkpoints?: Array<{ key: string; status?: string }> | null;
}): string {
  const key = job.currentCheckpoint;
  if (!key) return "—";
  const label = CHECKPOINT_LABELS[key] ?? key;
  const cps = job.checkpoints;
  const cp = Array.isArray(cps) ? cps.find((c) => c && c.key === key) : null;
  const status = cp && typeof cp.status === "string" ? cp.status : null;
  if (status === "pending") {
    return `${label} (started, not completed)`;
  }
  if (status === "fail") {
    return `${label} (failed)`;
  }
  return label;
}

export function isGovernedJob(
  job: Pick<ProcessingJobRow, "ownership"> | null | undefined,
): boolean {
  return job?.ownership === "governed";
}
